import {
  AlertTriangle,
  BadgeCheck,
  BriefcaseIcon,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Euro,
  FilePen,
  FileText,
  FileUp,
  Globe,
  Hash,
  Home,
  Key,
  Mail,
  MapPin,
  Maximize2,
  PartyPopper,
  Phone,
  Scale,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Store,
  User,
  Users,
  XCircle,
} from 'lucide-react';
import type {
  ConditionalVisibility,
  Field,
  FieldShortcut,
  FormSectionsWithShortcuts,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import { policyControllerPushPolicyEvent } from 'korint-sdk';
import { formatPrice } from '@folio/lib';
import _ from 'lodash';
import { AssetClaimsModule, InvoicesScheduleModule, QuoteFunnelModule } from '@folio/modules';
import { ContactKey } from '@folio/utils/customerDisplayName';
import { TUTASSUR_RECAP_SHORT_LABELS } from './common';
import z, { type ZodTypeAny } from 'zod';

const showWhenCompanyExists = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'false',
  },
] as const satisfies ConditionalVisibility[];

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'true',
  },
] as const satisfies ConditionalVisibility[];

const financialCompanySections: FormSectionsWithShortcuts = [
  {
    title: "Informations financières de l'année dernière ou projection",
    fields: [{ name: 'companyRevenue', icon: Euro }],
  },
];

const companyFunnelSections = [
  {
    title: "Informations de l'entreprise",
    fields: [
      {
        name: 'isCompanyBeingCreated',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.isCompanyBeingCreated ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isCompanyBeingCreated === 'true',
      },
      {
        name: 'companySiret',
        required: false,
        type: 'company',
        displayCompanyInformation: true,
        icon: Search,
        apiDataAccessor: apiData =>
          apiData.customFields?.companySiret
            ? {
                siret: apiData?.customFields?.companySiret,
                name: apiData?.customFields?.companyName,
                location: {
                  postCode: apiData?.customFields?.companyHeadquarterPostcode,
                  city: apiData?.customFields?.companyCity,
                  address: apiData?.customFields?.companyAddress,
                },
                nafCode: apiData?.customFields?.companyNafCode,
              }
            : null,
        formDataAccessor: 'companySiret.siret',
        isCustomField: true,
        showWhen: showWhenCompanyExists,
      },
      {
        name: 'companyName',
        required: true,
        type: 'text',
        DEPRECATED_placeholder: 'Acme SAS',
        icon: Building2,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyAddress',
        required: true,
        type: 'address',
        icon: MapPin,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterPostcode',
        required: true,
        type: 'postcode',
        countries: ['FR'],
        colSpan: 4 as const,
        icon: Hash,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyCity',
        required: true,
        type: 'city',
        colSpan: 8 as const,
        icon: Building2,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyLegalStatus',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'SAS, SARL, SA...',
        icon: FileText,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyNafCode',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        DEPRECATED_placeholder: '6201Z',
        icon: Hash,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyCreationDate',
        required: true,
        type: 'date',
        icon: CalendarDays,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyStaffCount',
        required: true,
        type: 'number',
        colSpan: 6 as const,
        DEPRECATED_placeholder: '10',
        icon: Users,
        isCustomField: true,
      },
      {
        name: 'companyActivity',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        icon: BriefcaseIcon,
        isCustomField: true,
      },
      {
        name: 'useCompanyAddress',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.useCompanyAddress ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.useCompanyAddress === 'true',
      },
      {
        name: 'contactAddress',
        type: 'computed',
        dependsOn: ['useCompanyAddress', 'companyAddress', 'companySiret'],
        hidden: true,
        required: false,
        isCustomField: true,
        formDataAccessor: formData => {
          if (!formData.useCompanyAddress) {
            return undefined;
          }

          return _.get(formData, 'companySiret.location.address') ?? formData.companyAddress;
        },
      },
      {
        name: 'contactPostalCode',
        type: 'computed',
        dependsOn: ['useCompanyAddress', 'companyHeadquarterPostcode', 'companySiret'],
        hidden: true,
        required: false,
        isCustomField: true,
        formDataAccessor: formData => {
          if (!formData.useCompanyAddress) {
            return undefined;
          }

          return (
            _.get(formData, 'companySiret.location.postCode') ?? formData.companyHeadquarterPostcode
          );
        },
      },
      {
        name: 'contactCity',
        type: 'computed',
        dependsOn: ['useCompanyAddress', 'companyCity', 'companySiret'],
        hidden: true,
        required: false,
        isCustomField: true,
        formDataAccessor: formData => {
          if (!formData.useCompanyAddress) {
            return undefined;
          }

          return _.get(formData, 'companySiret.location.city') ?? formData.companyCity;
        },
      },
      {
        name: 'contactCountry',
        type: 'computed',
        dependsOn: ['useCompanyAddress', 'companyCountry', 'companySiret'],
        hidden: true,
        required: false,
        isCustomField: true,
        formDataAccessor: formData => {
          if (!formData.useCompanyAddress) {
            return undefined;
          }

          return _.get(formData, 'companySiret.location.country') ?? formData.companyCountry;
        },
      },
    ],
  },
  ...financialCompanySections,
] as const satisfies FormSectionsWithShortcuts;

const contactFunnelSections = [
  {
    title: 'Informations du contact',
    fields: [
      {
        name: 'contactCivility',
        required: true,
        type: 'select',
        options: [
          { value: 'MONSIEUR', label: 'Monsieur' },
          { value: 'MADAME', label: 'Madame' },
          { value: 'MADEMOISELLE', label: 'Mademoiselle' },
        ],
        icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User },
        isCustomField: true,
      },
      {
        name: 'contactFirstName',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        icon: User,
        isCustomField: true,
      },
      {
        name: 'contactLastName',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        icon: User,
        isCustomField: true,
      },
      {
        name: 'contactEmail',
        required: true,
        type: 'email',
        colSpan: 6 as const,
        icon: Mail,
        isCustomField: true,
      },
      {
        name: 'contactPhone',
        required: true,
        type: 'phoneNumber',
        colSpan: 6 as const,
        DEPRECATED_placeholder: '6 00 00 00 00',
        icon: Phone,
        countries: ['FR'],
        isCustomField: true,
      },
      {
        name: 'contactAddress',
        required: true,
        type: 'address',
        icon: MapPin,
        isCustomField: true,
      },
      {
        name: 'contactPostalCode',
        required: true,
        type: 'postcode',
        colSpan: 4 as const,
        DEPRECATED_placeholder: '75001',
        icon: Hash,
        isCustomField: true,
      },
      {
        name: 'contactCity',
        required: true,
        type: 'city',
        colSpan: 8 as const,
        icon: Building2,
        isCustomField: true,
      },
      {
        name: 'contactCountry',
        required: true,
        type: 'country',
        icon: Globe,
        isCustomField: true,
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const previousInsurerSections = [
  {
    title: "Historique d'assurance",
    fields: [
      {
        name: 'hasTheCompanyBeenInsuredInTheLast36Months',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: ShieldCheck,
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.hasTheCompanyBeenInsuredInTheLast36Months ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasTheCompanyBeenInsuredInTheLast36Months === 'true',
      },
      {
        name: 'previousInsuranceCompanyName',
        required: true,
        type: 'text',
        DEPRECATED_placeholder: 'Groupama, AXA...',
        icon: Building2,
        isCustomField: true,
        showWhen: [
          {
            fieldName: 'hasTheCompanyBeenInsuredInTheLast36Months',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
      {
        name: 'previousInsuranceContractNumber',
        required: false,
        type: 'text',
        DEPRECATED_placeholder: 'XX-XXXXXX',
        icon: Hash,
        isCustomField: true,
        showWhen: [
          {
            fieldName: 'hasTheCompanyBeenInsuredInTheLast36Months',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
      {
        name: 'companyCancellation',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: XCircle,
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.companyCancellation ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.companyCancellation === 'true',
        showWhen: [
          {
            fieldName: 'hasTheCompanyBeenInsuredInTheLast36Months',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
      {
        name: 'cancellationDate',
        required: false,
        type: 'date',
        icon: CalendarDays,
        isCustomField: true,
        showWhen: [
          {
            fieldName: 'hasTheCompanyBeenInsuredInTheLast36Months',
            condition: 'equals',
            value: 'true',
          },
          {
            fieldName: 'companyCancellation',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
      {
        name: 'cancellationReason',
        required: true,
        type: 'select',
        options: [
          { value: 'OTHER', label: 'Autre' },
          { value: 'JUDICIAL_LIQUIDATION', label: 'Liquidation judiciaire' },
          { value: 'NON_PAYMENT', label: 'Non-paiement' },
          { value: 'JUDICIAL_RECOVERY', label: 'Redressement judiciaire' },
          { value: 'DISASTERS', label: 'Sinistres' },
        ],
        icons: {
          OTHER: XCircle,
          JUDICIAL_LIQUIDATION: Scale,
          NON_PAYMENT: Euro,
          JUDICIAL_RECOVERY: Scale,
          DISASTERS: AlertTriangle,
        },
        perRow: 3,
        isCustomField: true,
        showWhen: [
          {
            fieldName: 'hasTheCompanyBeenInsuredInTheLast36Months',
            condition: 'equals',
            value: 'true',
          },
          {
            fieldName: 'companyCancellation',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const quoteAssetFields: FieldShortcut[] = [
  {
    name: 'startedAt',
    required: true,
    type: 'datetime',
    min: 'now',
    icon: CalendarDays,
  },
];

const nonDelegatedQuotePolicySections = [
  {
    title: 'Options de garantie',
    fields: [
      {
        name: 'insuredContentValue',
        required: true,
        type: 'number',
        colSpan: 6 as const,
        DEPRECATED_placeholder: '50 000',
        icon: Euro,
        isCustomField: true,
      },
      {
        name: 'chosenExcess',
        required: true,
        type: 'number',
        colSpan: 6 as const,
        DEPRECATED_placeholder: '500',
        icon: Euro,
        isCustomField: true,
      },
      {
        name: 'chosenLegalProtection',
        required: true,
        type: 'select',
        options: [
          { value: 'NONE', label: 'Aucune' },
          { value: 'BASIC_FORMULA', label: 'Protection juridique essentielle' },
          { value: 'EXTENDED_FORMULA', label: 'Protection juridique summum' },
        ],
        icons: {
          NONE: XCircle,
          BASIC_FORMULA: Shield,
          EXTENDED_FORMULA: ShieldCheck,
        },
        isCustomField: true,
      },
      {
        name: 'chosenProfessionalCivilLiability',
        required: true,
        type: 'select',
        options: [
          { value: 'NONE', label: 'Aucune' },
          { value: 'FIFTY_THOUSAND', label: '50 000 €' },
          { value: 'ONE_HUNDRED_THOUSAND', label: '100 000 €' },
          { value: 'ONE_HUNDRED_FIFTY_THOUSAND', label: '150 000 €' },
          { value: 'TWO_HUNDRED_THOUSAND', label: '200 000 €' },
          { value: 'THREE_HUNDRED_THOUSAND', label: '300 000 €' },
          { value: 'FIVE_HUNDRED_THOUSAND', label: '500 000 €' },
          { value: 'SEVEN_HUNDRED_FIFTY_THOUSAND', label: '750 000 €' },
        ],
        icons: {
          NONE: XCircle,
          FIFTY_THOUSAND: Euro,
          ONE_HUNDRED_THOUSAND: Euro,
          ONE_HUNDRED_FIFTY_THOUSAND: Euro,
          TWO_HUNDRED_THOUSAND: Euro,
          THREE_HUNDRED_THOUSAND: Euro,
          FIVE_HUNDRED_THOUSAND: Euro,
          SEVEN_HUNDRED_FIFTY_THOUSAND: Euro,
        },
        perRow: 4,
        isCustomField: true,
      },
      {
        name: 'chosenExploitationCivilLiability',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Shield,
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.chosenExploitationCivilLiability ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.chosenExploitationCivilLiability === 'true',
      },
      {
        name: 'chosenDirectorsLiability',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Users,
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.chosenDirectorsLiability ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.chosenDirectorsLiability === 'true',
      },
      {
        name: 'chosenMobilityOption',
        type: 'radio',
        display: 'toggle' as const,
        required: true,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: BriefcaseIcon,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.chosenMobilityOption ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.chosenMobilityOption === 'true',
      },
      {
        name: 'chosenMobilityAmount',
        required: false,
        type: 'number',
        DEPRECATED_placeholder: '5 000',
        icon: Euro,
        isCustomField: true,
        showWhen: [
          {
            fieldName: 'chosenMobilityOption',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
      {
        name: 'chosenExploitationLoss',
        required: true,
        type: 'select',
        options: [
          { value: 'NONE', label: 'Aucune' },
          { value: 'TWELVE_MONTHS', label: '12 mois' },
          { value: 'EIGHTEEN_MONTHS', label: '18 mois' },
          { value: 'TWENTY_FOUR_MONTHS', label: '24 mois' },
        ],
        icons: {
          NONE: XCircle,
          TWELVE_MONTHS: CalendarDays,
          EIGHTEEN_MONTHS: CalendarCheck,
          TWENTY_FOUR_MONTHS: CalendarRange,
        },
        isCustomField: true,
      },
    ] as const satisfies Array<Field>,
  },
  {
    title: 'Montants de garantie personnalisés',
    fields: [
      {
        name: 'guaranteeAmountFireExplosionStorm',
        required: true,
        type: 'number',
        DEPRECATED_placeholder: '200 000',
        icon: Euro,
        isCustomField: true,
      },
      {
        name: 'guaranteeAmountWaterDamage',
        required: true,
        type: 'number',
        DEPRECATED_placeholder: '100 000',
        icon: Euro,
        isCustomField: true,
      },
      {
        name: 'guaranteeAmountTheftVandalism',
        required: true,
        type: 'number',
        DEPRECATED_placeholder: '50 000',
        icon: Euro,
        isCustomField: true,
      },
      {
        name: 'guaranteeAmountGlassBreakage',
        required: true,
        type: 'number',
        DEPRECATED_placeholder: '10 000',
        icon: Euro,
        isCustomField: true,
      },
      {
        name: 'guaranteeAmountMachineBreakdown',
        required: false,
        type: 'number',
        DEPRECATED_placeholder: '20 000',
        icon: Euro,
        isCustomField: true,
      },
      {
        name: 'guaranteeAmountExternalAmenities',
        required: false,
        type: 'number',
        DEPRECATED_placeholder: '5 000',
        icon: Euro,
        isCustomField: true,
      },
      {
        name: 'guaranteeAmountRefrigeratedGoods',
        required: false,
        type: 'number',
        DEPRECATED_placeholder: '10 000',
        icon: Euro,
        isCustomField: true,
      },
    ] as const satisfies Array<Field>,
  },
] as const satisfies FormSectionsWithShortcuts;

const KILLING_MESSAGE = 'Il est impossible de souscrire une police dans ce cas';

const killingIfFalse = {
  blockingValue: 'false',
  blockingErrorMessage: KILLING_MESSAGE,
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(z.literal('true', { errorMap: () => ({ message: KILLING_MESSAGE }) })),
};

const killingIfTrue = {
  blockingValue: 'true',
  blockingErrorMessage: KILLING_MESSAGE,
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(z.literal('false', { errorMap: () => ({ message: KILLING_MESSAGE }) })),
};

const additionalQuestionsSections = [
  {
    fields: [
      {
        name: 'hasHardMaterialsAndIsNotTemporary',
        ...killingIfFalse,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Building2,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasHardMaterialsAndIsNotTemporary ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasHardMaterialsAndIsNotTemporary === 'true',
      },
      {
        name: 'isInGoodCondition',
        ...killingIfFalse,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: ShieldCheck,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isInGoodCondition ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isInGoodCondition === 'true',
      },
      {
        name: 'hasWaterNetworkIfOver500mAltitude',
        ...killingIfFalse,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: MapPin,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasWaterNetworkIfOver500mAltitude ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasWaterNetworkIfOver500mAltitude === 'true',
      },
      {
        name: 'isAccessibleWithin30Minutes',
        ...killingIfFalse,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: MapPin,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isAccessibleWithin30Minutes ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isAccessibleWithin30Minutes === 'true',
      },
      {
        name: 'isInMetropolitanFrance',
        ...killingIfFalse,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Globe,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isInMetropolitanFrance ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isInMetropolitanFrance === 'true',
      },
      {
        name: 'isLCISufficient',
        ...killingIfFalse,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Euro,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isLCISufficient ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isLCISufficient === 'true',
      },
    ],
  },
  {
    fields: [
      {
        name: 'isWoodenStructure',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Building2,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isWoodenStructure ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isWoodenStructure === 'true',
      },
      {
        name: 'isClosedMoreThan60Days',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: CalendarDays,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isClosedMoreThan60Days ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isClosedMoreThan60Days === 'true',
      },
      {
        name: 'isInShoppingCenterOver3000m2',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Store,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isInShoppingCenterOver3000m2 ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isInShoppingCenterOver3000m2 === 'true',
      },
      {
        name: 'isHistoricMonument',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Building2,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isHistoricMonument ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isHistoricMonument === 'true',
      },
      {
        name: 'isContentValueExceedsFFBIndex',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Euro,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isContentValueExceedsFFBIndex ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isContentValueExceedsFFBIndex === 'true',
      },
      {
        name: 'hasRestrictedActivities',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: AlertTriangle,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasRestrictedActivities ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasRestrictedActivities === 'true',
      },
      {
        name: 'hasReligiousPoliticalUnionActivity',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Users,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasReligiousPoliticalUnionActivity ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasReligiousPoliticalUnionActivity === 'true',
      },
      {
        name: 'isWithin10mOfIndustrialComplex',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: MapPin,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isWithin10mOfIndustrialComplex ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isWithin10mOfIndustrialComplex === 'true',
      },
      {
        name: 'isInArtisanCommercialIndustrialZone',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Store,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isInArtisanCommercialIndustrialZone ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isInArtisanCommercialIndustrialZone === 'true',
      },
      {
        name: 'isDisused',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: XCircle,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isDisused ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) => formData.isDisused === 'true',
      },
      {
        name: 'hasLargeColdRoomsOrFlammableLiquids',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: AlertTriangle,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasLargeColdRoomsOrFlammableLiquids ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasLargeColdRoomsOrFlammableLiquids === 'true',
      },
      {
        name: 'isUnderRenovation',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Building2,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isUnderRenovation ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isUnderRenovation === 'true',
      },
      {
        name: 'isBuildingOver28mOrLargeComplex',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Building2,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isBuildingOver28mOrLargeComplex ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isBuildingOver28mOrLargeComplex === 'true',
      },
      {
        name: 'hasGraffitiAsbestosOrNonStandardRoofing',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: AlertTriangle,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasGraffitiAsbestosOrNonStandardRoofing ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasGraffitiAsbestosOrNonStandardRoofing === 'true',
      },
      {
        name: 'hasFireClaimInLast60Months',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: AlertTriangle,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasFireClaimInLast60Months ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasFireClaimInLast60Months === 'true',
      },
      {
        name: 'hasHighRiskActivity',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: ShieldAlert,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasHighRiskActivity ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasHighRiskActivity === 'true',
      },
      {
        name: 'hasDecennialLiabilityActivity',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Scale,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasDecennialLiabilityActivity ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasDecennialLiabilityActivity === 'true',
      },
      {
        name: 'isInQPV',
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: MapPin,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isInQPV ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) => formData.isInQPV === 'true',
      },
      {
        name: 'isClassifiedCastle',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Building2,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isClassifiedCastle ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isClassifiedCastle === 'true',
      },
      {
        name: 'hasBeenTerminatedByTutassur',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: XCircle,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasBeenTerminatedByTutassur ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasBeenTerminatedByTutassur === 'true',
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const assetFields = [
  {
    name: 'assetAddress',
    required: true,
    type: 'address',
    icon: MapPin,
    isCustomField: true,
  },
  {
    name: 'assetAddressComplement',
    required: false,
    type: 'text',
    icon: MapPin,
    isCustomField: true,
  },
  {
    name: 'assetPostalCode',
    required: true,
    type: 'postcode',
    colSpan: 4 as const,
    icon: Hash,
    isCustomField: true,
  },
  {
    name: 'assetCity',
    required: true,
    type: 'city',
    colSpan: 8 as const,
    icon: Building2,
    isCustomField: true,
  },
  {
    name: 'assetCountry',
    required: true,
    type: 'country',
    DEPRECATED_placeholder: 'France',
    icon: Globe,
    isCustomField: true,
  },
  {
    name: 'assetSurface',
    required: true,
    type: 'number',
    icon: Maximize2,
    isCustomField: true,
  },
  {
    name: 'assetOccupantStatus',
    required: true,
    type: 'select',
    options: [
      { value: 'PROPRIETAIRE', label: 'Propriétaire' },
      { value: 'LOCATAIRE', label: 'Locataire' },
      { value: 'OCCUPANT_GRATUIT', label: 'Occupant gratuit' },
    ],
    icons: {
      PROPRIETAIRE: Home,
      LOCATAIRE: Key,
      OCCUPANT_GRATUIT: User,
    },
    isCustomField: true,
  },
  {
    name: 'isSiteIsolated',
    required: false,
    type: 'radio',
    display: 'toggle' as const,
    options: [
      { value: 'true', label: 'Oui' },
      { value: 'false', label: 'Non' },
    ],
    isCustomField: true,
    apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
      apiData.customFields?.isSiteIsolated ? 'true' : 'false',
    formDataAccessor: (formData: Record<string, unknown>) => formData.isSiteIsolated === 'true',
  },
  {
    name: 'name',
    required: false,
    type: 'computed',
    dependsOn: ['assetAddress'],
    hidden: true,
    formDataAccessor: (formData: Record<string, unknown>) => {
      return { name: _.get(formData, 'assetAddress') as string | undefined };
    },
  },
] as const satisfies Array<Field>;

const productCustomPath = '/multirisques-pro-sur-mesure';

const manualComponentsGroups: Array<{
  id: string;
  componentIds: string[];
  perRow?: number;
  colSpanOverrides?: Record<string, number>;
  placeholders?: Record<string, string>;
}> = [
  {
    id: 'premium',
    componentIds: ['PREMIUM', 'WHOLESALE_COMMISSION', 'INSURANCE_TAX'],
    perRow: 2,
    colSpanOverrides: { INSURANCE_TAX: 12 },
    placeholders: {
      PREMIUM: '10 000',
      WHOLESALE_COMMISSION: '1 500',
      INSURANCE_TAX: '350',
    },
  },
  {
    id: 'wholesaleFees',
    componentIds: ['WHOLESALE_ADDITIONAL_COMMISSION', 'WHOLESALE_SUBSCRIPTION_FEE', 'RENEWAL_FEE'],
    perRow: 2,
    colSpanOverrides: { RENEWAL_FEE: 12 },
    placeholders: {
      WHOLESALE_ADDITIONAL_COMMISSION: '5',
      WHOLESALE_SUBSCRIPTION_FEE: '150',
      RENEWAL_FEE: '80',
    },
  },
  {
    id: 'retailFees',
    componentIds: ['RETAIL_COMMISSION', 'RETAIL_SUBSCRIPTION_FEE', 'RETAIL_MANAGEMENT_FEE'],
    perRow: 2,
    colSpanOverrides: { RETAIL_MANAGEMENT_FEE: 12 },
    placeholders: {
      RETAIL_COMMISSION: '10',
      RETAIL_SUBSCRIPTION_FEE: '100',
      RETAIL_MANAGEMENT_FEE: '200',
    },
  },
];

const BILLING_FREQUENCY_ICONS = {
  MONTHLY: CalendarDays,
  QUARTERLY: CalendarRange,
  HALF_YEARLY: CalendarClock,
  YEARLY: CalendarCheck,
};

export const tutassurMilaMultirisquesProConfig = {
  display: {
    category: 'professionnel',
    icon: BriefcaseIcon,
    badges: ['custom'],
  },
  productCustomPath,
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: Building2,
        sections: companyFunnelSections,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'asset',
        type: 'assetFunnel',
        icon: Store,
        maxAssets: 1,
        fields: assetFields,
      },
      {
        path: 'contact',
        type: 'customerFunnel',
        icon: User,
        sections: contactFunnelSections,
      },
      {
        path: 'previous-insurer',
        type: 'customerFunnel',
        icon: ShieldCheck,
        sections: previousInsurerSections,
      },
      {
        path: 'claims',
        type: 'module',
        icon: AlertTriangle,
        module: <AssetClaimsModule />,
      },
      {
        path: 'additional-questions',
        type: 'policyFunnel',
        icon: ShieldAlert,
        sections: additionalQuestionsSections,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            assetFields={quoteAssetFields}
            policySections={nonDelegatedQuotePolicySections}
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
        additionalComponents: {
          isQuoteTierModuleVisible: false,
        },
      },
    ],
  },
  mtaFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: Building2,
        sections: companyFunnelSections,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'asset',
        type: 'assetFunnel',
        icon: Store,
        maxAssets: 1,
        fields: assetFields,
      },
      {
        path: 'contact',
        type: 'customerFunnel',
        icon: User,
        sections: contactFunnelSections,
      },
      {
        path: 'previous-insurer',
        type: 'customerFunnel',
        icon: ShieldCheck,
        sections: previousInsurerSections,
      },
      {
        path: 'claims',
        type: 'module',
        icon: AlertTriangle,
        module: <AssetClaimsModule />,
      },
      {
        path: 'additional-questions',
        type: 'policyFunnel',
        icon: ShieldAlert,
        sections: additionalQuestionsSections,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            assetFields={quoteAssetFields}
            policySections={nonDelegatedQuotePolicySections}
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        callback: {},
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
      },
    ],
  },
  policyholderRenewalFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: Building2,
        sections: financialCompanySections,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'renewal-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
        redirectTo: ({ policyId }) => `/policyholder/${policyId}/information`,
      },
    ],
  },
  renewalFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: Building2,
        sections: financialCompanySections,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            policySections={nonDelegatedQuotePolicySections}
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'renewal-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
        redirectTo: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}`,
      },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      {
        key: 'companyName',
        type: 'text',
        contactKey: ContactKey.COMPANY_NAME,
      },
      { key: 'companyAddress', type: 'text' },
      { key: 'companyHeadquarterPostcode', type: 'postcode', countries: ['FR'] },
      { key: 'companyCity', type: 'text' },
      { key: 'companySiret', type: 'text' },
      { key: 'companyNafCode', type: 'text' },
      { key: 'companyRevenue', type: 'number' },
      { key: 'companyStaffCount', type: 'number' },
      { key: 'companyActivity', type: 'text' },
      { key: 'isCompanyBeingCreated', type: 'boolean' },
      {
        key: 'contactFirstName',
        type: 'text',
        contactKey: ContactKey.FIRST_NAME,
      },
      { key: 'contactLastName', type: 'text', contactKey: ContactKey.LAST_NAME },
      { key: 'contactEmail', type: 'text' },
      { key: 'contactPhone', type: 'text' },
      {
        key: 'contactCivility',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const civilityMap: Record<string, string> = {
            MONSIEUR: 'Monsieur',
            MADAME: 'Madame',
            MADEMOISELLE: 'Mademoiselle',
          };
          return civilityMap[value] ?? value;
        },
      },
      { key: 'contactAddress', type: 'text' },
      { key: 'contactPostalCode', type: 'text' },
      { key: 'contactCity', type: 'text' },
      { key: 'contactCountry', type: 'text' },
    ],
  },
  assetInformations: {
    fieldsConfig: [
      { key: 'assetAddressComplement', type: 'text' },
      {
        key: 'assetOccupantStatus',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const occupantStatusMap: Record<string, string> = {
            PROPRIETAIRE: 'Propriétaire',
            LOCATAIRE: 'Locataire',
            OCCUPANT_GRATUIT: 'Occupant gratuit',
          };
          return occupantStatusMap[value] ?? value;
        },
      },
      { key: 'isSiteIsolated', type: 'boolean' },
    ],
  },
  policyInformationCard: {
    title: 'Informations de la police',
    fieldsConfig: [
      {
        key: 'allClientDocumentsOk',
        type: 'text',
      },
      {
        key: 'insuredContentValue',
        type: 'number',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'number') return undefined;
          return formatPrice(value, 1);
        },
      },
      {
        key: 'chosenExcess',
        type: 'number',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'number') return undefined;
          return formatPrice(value, 1);
        },
      },
      {
        key: 'chosenLegalProtection',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const legalProtectionMap: Record<string, string> = {
            BASIC_FORMULA: 'Protection juridique basique',
            EXTENDED_FORMULA: 'Protection juridique étendue',
            NONE: 'Aucune',
          };
          return legalProtectionMap[value] ?? value;
        },
      },
      {
        key: 'chosenProfessionalCivilLiability',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const professionalCivilLiabilityMap: Record<string, string> = {
            NONE: 'Aucune',
            FIFTY_THOUSAND: '50 000 €',
            ONE_HUNDRED_THOUSAND: '100 000 €',
            ONE_HUNDRED_FIFTY_THOUSAND: '150 000 €',
            TWO_HUNDRED_THOUSAND: '200 000 €',
            THREE_HUNDRED_THOUSAND: '300 000 €',
            FIVE_HUNDRED_THOUSAND: '500 000 €',
            SEVEN_HUNDRED_FIFTY_THOUSAND: '750 000 €',
          };
          return professionalCivilLiabilityMap[value] ?? value;
        },
      },
      {
        key: 'chosenExploitationCivilLiability',
        type: 'boolean',
      },
      {
        key: 'chosenMobility',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const mobilityAmountMap: Record<string, string> = {
            NONE: 'Aucune',
            FIVE_THOUSAND: '5 000 €',
            TEN_THOUSAND: '10 000 €',
          };
          return mobilityAmountMap[value] ?? value;
        },
      },
      {
        key: 'chosenExploitationLoss',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const exploitationLossDurationMap: Record<string, string> = {
            NONE: 'Aucune',
            TWELVE_MONTHS: '12 mois',
            EIGHTEEN_MONTHS: '18 mois',
            TWENTY_FOUR_MONTHS: '24 mois',
          };
          return exploitationLossDurationMap[value] ?? value;
        },
      },
      {
        key: 'chosenDirectorsLiability',
        type: 'boolean',
      },
      {
        key: 'guaranteeAmountFireExplosionStorm',
        type: 'number',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'number') return undefined;
          return formatPrice(value, 1);
        },
      },
      {
        key: 'guaranteeAmountWaterDamage',
        type: 'number',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'number') return undefined;
          return formatPrice(value, 1);
        },
      },
      {
        key: 'guaranteeAmountTheftVandalism',
        type: 'number',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'number') return undefined;
          return formatPrice(value, 1);
        },
      },
      {
        key: 'guaranteeAmountGlassBreakage',
        type: 'number',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'number') return undefined;
          return formatPrice(value, 1);
        },
      },
      {
        key: 'guaranteeAmountMachineBreakdown',
        type: 'number',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'number') return undefined;
          return formatPrice(value, 1);
        },
      },
      {
        key: 'guaranteeAmountExternalAmenities',
        type: 'number',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'number') return undefined;
          return formatPrice(value, 1);
        },
      },
      {
        key: 'guaranteeAmountRefrigeratedGoods',
        type: 'number',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'number') return undefined;
          return formatPrice(value, 1);
        },
      },
    ],
  },
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery${productCustomPath}/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: true,
  },
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
    customModules: [],
  },
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'TOTAL_PREMIUM', variant: 'dimmed' }] },
      { lines: [{ key: 'BROKERAGE_FEES' }] },
      { lines: [{ key: 'MANAGEMENT_FEE' }] },
      {
        lines: [{ key: 'SUBSCRIPTION_FEE' }, { key: 'RENEWAL_FEE', tooltip: true }],
      },
    ],
  },
  policyholderInvitationModuleConfig: {
    relationTypes: ['DRIVER'],
  },
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-supporting-validation',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'wholesaler-quote-uploaded',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ documents }) =>
          documents.some(({ type, category }) => type === 'non_delegated' && category === 'QUOTE'),
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: true,
    enablePolicyComparison: true,
    quoteDerogationFunnelModule: {
      afterEndPath: '/derogations',
      steps: [
        {
          path: 'quote',
          type: 'custom',
          icon: Shield,
          component: (
            <QuoteFunnelModule
              assetFields={quoteAssetFields}
              policySections={nonDelegatedQuotePolicySections}
              nonDelegatedDocuments={['QUOTE']}
              manualComponentsGroups={manualComponentsGroups}
              recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
            />
          ),
        },
      ],
    },
  },
  claims: {
    claimsCategories: [
      {
        category: 'FIRE_AND_EXPLOSION',
        label: 'Incendies, explosions et événements assimilés',
      },
      {
        category: 'ELECTRICAL_DAMAGE',
        label: 'Dommages électriques',
      },
      {
        category: 'STORED_GOODS',
        label: 'Marchandises conservées',
      },
      {
        category: 'WATER_DAMAGE',
        label: 'Dégâts des eaux',
      },
      {
        category: 'THEFT_VANDALISM_ATTEMPTED_THEFT',
        label: 'Vol, vandalisme et tentative de vol',
      },
      {
        category: 'CLIMATIC_EVENTS',
        label: 'Évènements climatiques',
      },
      {
        category: 'NATURAL_DISASTERS',
        label: 'Catastrophes Naturelles',
      },
      {
        category: 'TERRORIST_ATTACKS',
        label: 'Attentats',
      },
      {
        category: 'ELECTRICAL_ELECTRONIC_EQUIPMENT_BREAKDOWN',
        label: 'Bris de matériel électrique et/ou électronique',
      },
      {
        category: 'LOSS_OF_GOODS_IN_CONTROLLED_ATMOSPHERE',
        label: 'Pertes de marchandises conservées en atmosphère contrôlée',
      },
      {
        category: 'EXPENSES_AND_LOSSES',
        label: 'Frais et pertes',
      },
      {
        category: 'PROFESSIONAL_PREMISES_LIABILITY',
        label:
          'Responsabilité du fait de vos locaux professionnels et de vos aménagements extérieurs',
      },
    ],
    fieldsConfig: [
      {
        name: 'amount',
        type: 'number',
        required: true,
        DEPRECATED_placeholder: '1 500',
        icon: Euro,
        isCustomField: true,
      },
    ],
  },
} satisfies KorintModulesProductsConfig;
